/**
 * Konfigurace zobrazení výsledků vyhledávání pro každou kategorii.
 *
 * Pole `field` odkazuje na vlastnost objektu `data` (response z API).
 * Pole začínající `_` jsou computed hodnoty připravené v komponentě (ngOnInit).
 *
 * Konvence pro `params`:
 *   - běžný string  → reference na data field:  'projectKey' → data.projectKey
 *   - prefix '$'     → literální hodnota:        '$about'    → 'about'
 *   - '$null'        → null
 *
 * Konvence pro `condition`:
 *   - undefined      → auto-check: field je zobrazen jen když data[field] je truthy
 *   - true           → vždy zobrazit
 *   - 'fieldName'    → data[fieldName] musí být truthy
 *   - 'field=value'  → data[field] === value
 *   - ['c1', 'c2']   → všechny podmínky musí platit (AND)
 */

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface FieldConfig {
  label: string;
  field: string;
  highlight?: boolean;              // default true
  suffix?: string;                  // text za hodnotou, např. ' m²'
  condition?: true | string | string[];
}

export interface SectionConfig {
  condition?: string | string[];    // undefined = always
  fields: FieldConfig[];
}

export interface ProjectLinkSingle {
  mode: 'single';
  state: string;
  params: { [key: string]: string };
}

export interface ProjectLinkArray {
  mode: 'array';
  state: string;
  params: { [key: string]: string };
}

export interface ProjectLinkConditional {
  mode: 'conditional';
  links: Array<{
    condition?: string;
    state: string;
    params: { [key: string]: string };
  }>;
}

export type ProjectLinkConfig = ProjectLinkSingle | ProjectLinkArray | ProjectLinkConditional;

export interface SubjectListConfig {
  label: string;                
  labelPlural?: string;         
  field: string;                // pole v data, hodnota je string oddělený '|'
}

export interface CategoryConfig {
  type: string;
  icon: string;
  label: string;
  nameField: string;
  nameFallback?: string;
  nameLink?: {
    state: string;
    params: { [key: string]: string };
  };
  showScoreValue: boolean;
  sections: SectionConfig[];
  projectLink?: ProjectLinkConfig;
  subjectList?: SubjectListConfig;
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

export const CATEGORY_CONFIGS: { [type: string]: CategoryConfig } = {

  // ── AKCE ────────────────────────────────────────────────────────────────
  project: {
    type: 'project',
    icon: 'fa fa-folder',
    label: 'Akce',
    nameField: 'name',
    nameLink: {
      state: 'symap.project.home',
      params: { projectKey: 'key' },
    },
    showScoreValue: true,
    sections: [
      {
        fields: [
          { label: 'Organizační jednotka', field: 'unitName'},
          { label: 'Typ akce', field: 'type' },
          { label: 'Číslo spisu', field: 'folder' },
          { label: 'Evidenční číslo', field: 'isprofond' },
          { label: 'Název úseku', field: 'section' },
          { label: 'Manažer akce', field: 'manager' }
        ],
      },
    ],
  },

  // ── VLASTNÍK ────────────────────────────────────────────────────────────
  subject: {
    type: 'subject',
    icon: 'fa fa-user',
    label: 'Vlastník',
    nameField: '_subjectName',
    showScoreValue: true,
    sections: [
      {
        fields: [
          { label: 'Osoba č.1', field: '_person1Name', condition: 'opsubType=BSM' },
          { label: 'Adresa', field: '_address1', condition: true },
          { label: 'Email', field: 'email' },
          { label: 'Telefon', field: 'phoneNumber' },
          { label: 'Rok narození', field: '_birthYear', highlight: false, condition: 'personalNumber' },
          { label: 'IČO', field: 'ico', condition: ['opsubType=OPO', 'ico'] },
        ],
      },
      {
        condition: 'opsubType=BSM',
        fields: [
          { label: 'Osoba č.2', field: '_person2Name', condition: true },
          { label: 'Adresa', field: '_address2', condition: true },
          { label: 'Email', field: 'email2' },
          { label: 'Telefon', field: 'phoneNumber2'},
          { label: 'Rok narození', field: '_birthYear2', highlight: false, condition: 'personalNumber2' },
        ],
      },
    ],
    projectLink: {
      mode: 'array',
      state: 'symap.project.owners.detail',
      params: { projectKey: 'projectKey', ownerId: 'subjectId', tab: '$about' },
    },
  },

  // ── PARCELA ─────────────────────────────────────────────────────────────
  parcel: {
    type: 'parcel',
    icon: 'fa fa-toggle-up',
    label: 'Parcela',
    nameField: '_parcelName',
    showScoreValue: true,
    sections: [
      {
        fields: [
          { label: 'Název k.ú.', field: 'katuzeNazev' },
          { label: 'Číslo LV', field: 'lv' },
          { label: 'Název číslovaní parcely', field: 'cislNazev' },
          { label: 'Výměra', field: 'vymera', suffix: ' m²' },
          { label: 'Druh pozemku', field: 'drupozNazev' },
        ],
      },
    ],
    projectLink: {
      mode: 'array',
      state: 'symap.project.parcels.detail',
      params: { projectKey: 'projectKey', id: 'parcelId', tab: '$tenant' },
    },
  },

  // ── LV ──────────────────────────────────────────────────────────────────
  title: {
    type: 'title',
    icon: 'fa fa-users',
    label: 'LV',
    nameField: 'lv',
    showScoreValue: true,
    sections: [
      {
        fields: [
          { label: 'Název k.ú.', field: 'katuzeNazev' },
        ],
      },
    ],
    projectLink: {
      mode: 'array',
      state: 'symap.project.titles.detail',
      params: { projectKey: 'projectKey', id: 'titleId', tab: '$null' },
    },
  },

  // ── GP ──────────────────────────────────────────────────────────────────
  geometric_plan: {
    type: 'geometric_plan',
    icon: 'fa fa-object-ungroup',
    label: 'GP',
    nameField: 'number',
    showScoreValue: true,
    sections: [
      {
        fields: [
          { label: 'Zhotovitel', field: 'author' },
          { label: 'Číslo spisu', field: 'folder' },
        ],
      },
    ],
    projectLink: {
      mode: 'single',
      state: 'symap.project.geometricPlanRequests.detail',
      params: { projectKey: 'projectKey', id: 'id', tab: '$parcels-budouc' },
    },
  },

  // ── ZP ──────────────────────────────────────────────────────────────────
  opinion: {
    type: 'opinion',
    icon: 'fa fa-book',
    label: 'ZP',
    nameField: 'number',
    showScoreValue: true,
    sections: [
      {
        fields: [
          { label: 'Znalec', field: 'author' },
          { label: 'Znalecká kancelář', field: 'office' },
          { label: 'Číslo spisu', field: 'folder' },
        ],
      },
    ],
    projectLink: {
      mode: 'single',
      state: 'symap.project.opinionRequests.detail',
      params: { projectKey: 'projectKey', id: 'id', tab: '$parcels' },
    },
  },

  // ── KOMPONENTY ──────────────────────────────────────────────────────────
  component: {
    type: 'component',
    icon: 'fa fa-file-o',
    label: 'Komponenta',
    nameField: 'name',
    showScoreValue: true,
    sections: [
      {
        fields: [
          { label: 'Typ komponenty', field: 'typeName' },
          { label: 'Název dokumentu EPD', field: 'documentName' },
          { label: 'PID dokumentu', field: 'documentBarCode' },
          { label: 'Č.j. dokumentu', field: 'documentReferenceNumber' },
        ],
      },
    ],
    projectLink: {
      mode: 'conditional',
      links: [
        { condition: 'caseId', state: 'symap.project.cases.detail', params: { projectKey: 'projectKey', id: 'caseId' } },
        { condition: 'opinionId', state: 'symap.project.opinionRequests.detail', params: { projectKey: 'projectKey', id: 'opinionId', tab: '$parcels' } },
        { condition: 'geometricPlanId', state: 'symap.project.geometricPlanRequests.detail', params: { projectKey: 'projectKey', id: 'geometricPlanId', tab: '$parcels-budouc' } },
        { state: 'symap.project.info', params: { projectKey: 'projectKey', tab: '$documents-epd' } },
      ],
    },
  },

  // ── PŘÍPADY ─────────────────────────────────────────────────────────────
  case: {
    type: 'case',
    icon: 'fa fa-file',
    label: 'Případ',
    nameField: 'caseNumber',
    nameFallback: 'bez čísla HeN',
    showScoreValue: true,
    sections: [
      {
        fields: [
          { label: 'Pracovní číslo', field: 'internalCaseNumber' },
          { label: 'Číslo spisu', field: 'folder' },
          { label: 'Řešitel případu', field: 'mandatary' },
          { label: 'Typ případu', field: 'obligationName' },
          { label: 'Stav případu', field: 'caseStatusName' },
        ],
      },
    ],
    projectLink: {
      mode: 'single',
      state: 'symap.project.cases.detail',
      params: { projectKey: 'projectKey', id: 'id' },
    },
    subjectList: {
      label: 'Vlastník',
      labelPlural: 'Vlastníci',
      field: 'subjectsNames',
    },
  },
};
